import DeclarationNumberInput from 'components/ui/composed/declarations/formInput/DeclarationNumberInput';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DeclarationField from 'views/declarations/common/DeclarationField';
import { getFormikProps } from 'views/declarations/utils/form-utils';
import { ValidationSchema, Validator, validators } from '../validations/validations';
import useFormUtils from '../../../../../hooks/useFormUtils';
import { AvailableForms } from '../../../../../hooks/useHideEntities';
import useNumberOfItemsModal from 'views/declarations/declaration-tabs/declaration-form/utils/useNumberOfItemsModal';
import useProducts from 'hooks/useProducts';
import { useOutletContext } from 'react-router-dom';

type Fields = 'goodsItemQuantity';

const matchesItemsOnDeclaration: Validator = async (_, formModel) => {
    const itemsLength = formModel.getValues().goodsShipment?.governmentAgencyGoodsItem?.length;
    const goodsItemQuantity = formModel.getValues().goodsItemQuantity;
    if (itemsLength === undefined) return undefined;
    if (goodsItemQuantity === undefined || goodsItemQuantity === null || goodsItemQuantity === '') return undefined;
    if (Number(goodsItemQuantity) < 1) return 'Must be greater than or equal to 1';
    if (Number(itemsLength) !== Number(goodsItemQuantity)) {
        return 'Number of items from the input does not match the number of items in the declaration.';
    }
    return undefined;
};

export const totalNumberOfItemsBlockValidation: ValidationSchema<Fields> = {
    childValidators: {
        goodsItemQuantity: [
            validators.required(),
            validators.number(),
            validators.min(1),
            validators.maxLength(3),
            matchesItemsOnDeclaration,
        ],
    },
};

export const totalNumberOfItemsBlockOptionalValidation: ValidationSchema<Fields> = {
    childValidators: {
        goodsItemQuantity: [validators.number(), validators.maxLength(3), matchesItemsOnDeclaration],
    },
};

interface Props extends BlockProps<Fields>, DeclarationBlockProps<AvailableForms, Fields> {}

const TotalNumberOfItemsBlock = ({ declarationOptions }: Props): ReactElement => {
    const { t } = useTranslation('form');
    const { formTypeForCds: formType } = useFormUtils();
    const { createCdsProduct, listCdsProducts } = useProducts();
    const { saveAsDraft } =
        useOutletContext<{ saveAsDraft: (withNotification: boolean, data?: any) => Promise<any> }>() ?? {};

    const ucc = useMemo(() => '1/9', []);

    const [handleNumberOfItems, modalContextHolder] = useNumberOfItemsModal({
        createProduct: createCdsProduct,
        listProducts: listCdsProducts,
        saveAsDraft,
    });

    return (
        <>
            <DeclarationField name="goodsItemQuantity">
                {({ form, field }) => (
                    <DeclarationNumberInput
                        required={declarationOptions?.[formType]?.fieldOptions?.goodsItemQuantity?.required}
                        {...getFormikProps(field.name, form)}
                        fieldEvents={{
                            onBlur(value) {
                                handleNumberOfItems(Number(value));
                            },
                        }}
                        refNumber={ucc}
                        label={t('numberOfItems')}
                        condensed
                    />
                )}
            </DeclarationField>
            {modalContextHolder}
        </>
    );
};

export default TotalNumberOfItemsBlock;
